import useTheme from 'hooks/useTheme'
import { Flex, Image, Box, Button, useMatchBreakpoints, Text } from 'vvs-uikit'
import { Link as ReactLink } from 'react-router-dom'
import styled from 'styled-components'
import { isAfter } from 'date-fns'
import { formatShortDate, formatMonth } from 'utils/dayjs'
import igos, { PROMOTIONAL_BANNER_IGO_ID, DEFAULT_IGO_SALE_DURATION } from 'config/constants/igos'
import { ContextApi, useTranslation } from 'contexts/Localization'

export const IGO_ID = PROMOTIONAL_BANNER_IGO_ID

const getCurrentPromotion = (t?: ContextApi['t']) => {
  const promotingIgo = igos.find((igo) => igo.id === IGO_ID)
  if (!promotingIgo) return null

  const igoStartTime = new Date(promotingIgo.saleDate)
  const igoEndTime = new Date(promotingIgo.saleEndDate ?? igoStartTime.getTime() + DEFAULT_IGO_SALE_DURATION)

  return {
    header: t?.('Initial Gem Offerings'),
    duration: `${formatShortDate(igoStartTime)} - ${formatShortDate(igoEndTime).replace(
      formatMonth(igoStartTime),
      '',
    )}`,
    eventHolder: '',
    eventHolderLogo: `/images/igo/${promotingIgo.address}/promotional_banner_logo.svg`,
    igoStartTime,
    igoEndTime,
  }
}

export const isExpired = () => {
  const currentPromotion = getCurrentPromotion()
  if (!currentPromotion) return true

  const now = new Date()
  const end = new Date(currentPromotion.igoEndTime)
  return isAfter(now, end)
}

export const IGOPromotionalBanner = ({ onClick }) => {
  const { t } = useTranslation()
  const currentPromotion = getCurrentPromotion(t)
  const { theme } = useTheme()
  const { isTablet, isDesktop } = useMatchBreakpoints()

  if (!currentPromotion) return null

  return (
    <StyledCard background="white">
      {isDesktop ? (
        <Flex flexDirection="row" alignItems="center" px="40px" justifyContent="space-between" height="100%">
          <Button as={ReactLink} to="/igo" height={60} width={150} marginRight={isDesktop || isTablet ? '20px' : '0px'}>
            {t('Learn More')}
          </Button>
          <Flex flexDirection="column" alignItems="start" height="100%" width="25%">
            <IGOText fontWeight={500} bold color={theme.colors.darkBlue}>
              {currentPromotion.header}
            </IGOText>
            <IGODate fontWeight={500} color="#ffbf00">
              {currentPromotion.duration}
            </IGODate>
          </Flex>
          <Bar />
          <Flex flexDirection="row" alignItems="center" width="20%" height="100%">
            <Image height={50} width={210} ml={3} mr={10} src={currentPromotion.eventHolderLogo} alt="FERRO Logo" />
            <IGOText fontWeight={1000} bold color={theme.colors.darkBlue}>
              {currentPromotion.eventHolder}
            </IGOText>
          </Flex>
          <Image
            height={98}
            width={310}
            src="/images/igo/igoDiamonds.png"
            alt="IGO diamonds"
            style={{ margin: '20px 20px 5px 20px' }}
          />
          <Box height="100%" width="20px" onClick={() => onClick()} marginRight="5px" marginLeft="5px">
            <BannerImage height={20} width={20} src="/images/userBanner/close.png" alt="close" />
          </Box>
        </Flex>
      ) : isTablet ? (
        <Flex flexDirection="row" alignItems="center" px="20px" justifyContent="space-between" height="100%">
          <Button as={ReactLink} to="/igo" height={60} width={140} marginRight={isDesktop || isTablet ? '20px' : '0px'}>
            {t('Learn More')}
          </Button>
          <Flex flexDirection="column" justifyContent="center" height="100%">
            <Flex>
              <IGOText fontWeight={600} bold color={theme.colors.darkBlue} style={{ alignSelf: 'center' }}>
                {currentPromotion.header}
              </IGOText>
            </Flex>
            <IGODate bold fontWeight={600} color="#ffbf00">
              {currentPromotion.duration}
            </IGODate>
          </Flex>
          <Bar />
          <Flex flexDirection="row" alignItems="center" width="20%" height="100%">
            <Image height={50} width={210} mr={10} src={currentPromotion.eventHolderLogo} alt="FERRO Logo" />
            <IGOText fontWeight={1000} bold color={theme.colors.darkBlue}>
              {currentPromotion.eventHolder}
            </IGOText>
          </Flex>
          <Image
            height={82}
            width={100}
            marginTop="14px"
            marginBottom="14px"
            src="/images/igo/mobileIgoDiamonds.png"
            alt="Mobile IGO Diamonds"
            style={{ marginRight: '20px' }}
          />
          <Box height="100%" width="20px" onClick={() => onClick()} marginRight="5px">
            <BannerImage height={20} width={20} src="/images/userBanner/close.png" alt="close" />
          </Box>
        </Flex>
      ) : (
        <Flex flexDirection="column" alignItems="start" justifyContent="space-between" p="10px">
          <Flex flexDirection="column" alignItems="stretch" style={{ zIndex: 1 }} width="100%">
            <Flex flexDirection="row" alignItems="center" height="100%" justifyContent="space-evenly" mb="16px">
              <TextFlex flexDirection="row" alignItems="center" height="100%" justifyContent="start">
                <Image height={40} width={160} mr={10} src={currentPromotion.eventHolderLogo} alt="FERRO Logo" />
                <IGOMobileText fontWeight={1000} bold color={theme.colors.darkBlue}>
                  {currentPromotion.eventHolder}
                </IGOMobileText>
              </TextFlex>
            </Flex>

            <Flex flexDirection="row">
              <Flex flexDirection="column" flex="1">
                <IGOMobileText fontWeight={800} bold color={theme.colors.darkBlue} marginBottom="6px">
                  {currentPromotion.header}
                </IGOMobileText>
                <IGODateMobile width="100%" bold fontWeight={600} color="#ffbf00" marginBottom="4px">
                  {currentPromotion.duration}
                </IGODateMobile>
              </Flex>
              <Button as={ReactLink} to="/igo">
                {t('Learn More')}
              </Button>
            </Flex>
          </Flex>

          <Box zIndex={10} position="absolute" top="10%" right="7%" onClick={() => onClick()}>
            <BannerImage height={30} width={30} src="/images/userBanner/close.png" alt="close" />
          </Box>
        </Flex>
      )}
    </StyledCard>
  )
}

// IGO Promotional Banner
const StyledCard = styled(Box)`
  border-radius: ${({ theme }) => theme.radii.card};
  width: 100%;
  padding: 0px;
  height: fit-content;
  max-width: 1200px;

  position: relative;
  margin: 10px 30px 20px;
  @media screen and (max-width: 576px) {
    padding: 20px;
  }
`

const BannerImage = styled(Image)`
  margin-bottom: 0px;
  @media screen and (max-width: 768px) {
    width: 350px;
    height: 120px;
  }
`

const IGOText = styled(Text)`
  color: #1e3f6f;
  font-family: Effra;

  font-size: 28px;
  @media screen and (max-width: 1200px) {
    font-size: 24px;
  }
  @media screen and (max-width: 820px) {
    font-size: 20px;
  }
  @media screen and (max-width: 768px) {
    font-size: 14px;
  }
`

const IGOMobileText = styled(Text)`
  font-size: 16px;
`

const IGODate = styled(Text)`
  font-family: Effra;

  font-size: 20px;
  @media screen and (max-width: 820px) {
    font-size: 18px;
  }
`

const IGODateMobile = styled(Text)`
  font-size: 30px;
  @media screen and (max-width: 576px) {
    font-size: 16px;
  }
`

const TextFlex = styled(Flex)`
  width: 100%;
  @media screen and (max-width: 576px) {
    width: 400px;
  }
  @media screen and (max-width: 576px) {
    width: 400px;
  }
  @media screen and (max-width: 445px) {
    width: 350px;
  }
  @media screen and (max-width: 400px) {
    width: 300px;
  }
  @media screen and (max-width: 320px) {
    width: 250px;
  }
`

const Bar = styled.div`
  border-right: 1.5px solid black;
  height: 70px;
  margin: 0 10px;
`
